import { NotificationChannel } from '@bybeam/notification-client';
import Comment from './comment.js';
import { FeatureName } from './feature.js';
import { NotificationTemplate } from './notificationTemplate.js';

export enum NotificationType {
  UnknownType = 'UnknownType',
  AuthenticationChangePrimaryEmail = 'AuthenticationChangePrimaryEmail',
  AuthenticationResetPassword = 'AuthenticationResetPassword',
  AuthenticationVerifyEmail = 'AuthenticationVerifyEmail',
  AuthenticationMagicLink = 'AuthenticationMagicLink',
  AuthenticationIdentityVerifyEmail = 'AuthenticationIdentityVerifyEmail',
  ApplicationDenied = 'ApplicationDenied',
  ApplicationIncomplete = 'ApplicationIncomplete',
  ApplicationSubmitted = 'ApplicationSubmitted',
  ApplicationWithdrawn = 'ApplicationWithdrawn',
  ApplicationApproved = 'ApplicationApproved',
  PaymentsClaimFunds = 'PaymentsClaimFunds',
  PaymentsFundsClaimed = 'PaymentsFundsClaimed',
  PaymentsFundsIssued = 'PaymentsFundsIssued',
  PaymentSentToOtherParty = 'PaymentSentToOtherParty',
  PaymentsApplicantPaymentReset = 'PaymentsApplicantPaymentReset',
  PaymentsAdvocatePaymentReset = 'PaymentsAdvocatePaymentReset',
  PaymentsRecurringPaymentInitiated = 'PaymentsRecurringPaymentInitiated',
  PaymentsRecurringPaymentsScheduled = 'PaymentsRecurringPaymentsScheduled',
  PaymentsRecurringPaymentsScheduledOtherParty = 'PaymentsRecurringPaymentsScheduledOtherParty',
  MultipartyInvitation = 'MultipartyInvitation',
  ProgramReferral = 'ProgramReferral',
  CaseComment = 'CaseComment',
  ParticipantCaseCommentAssignee = 'ParticipantCaseCommentAssignee',
  ParticipantCaseCommentSupport = 'ParticipantCaseCommentSupport',
  WelcomeMessage = 'WelcomeMessage',
  UnsubscribeChannelConfirmation = 'UnsubscribeChannelConfirmation',
  SendSMSHelp = 'SendSMSHelp',
  NewApplicationsReport = 'NewApplicationsReport',
}

export type PlatformNotificationType =
  | typeof NotificationType.ApplicationApproved
  | typeof NotificationType.PaymentsAdvocatePaymentReset
  | typeof NotificationType.PaymentsApplicantPaymentReset
  | typeof NotificationType.ApplicationDenied
  | typeof NotificationType.ApplicationIncomplete
  | typeof NotificationType.ApplicationSubmitted
  | typeof NotificationType.ApplicationWithdrawn
  | typeof NotificationType.AuthenticationChangePrimaryEmail
  | typeof NotificationType.PaymentsClaimFunds
  | typeof NotificationType.PaymentsFundsClaimed
  | typeof NotificationType.PaymentsFundsIssued
  | typeof NotificationType.PaymentSentToOtherParty
  | typeof NotificationType.ProgramReferral
  | typeof NotificationType.PaymentsRecurringPaymentInitiated
  | typeof NotificationType.PaymentsRecurringPaymentsScheduled
  | typeof NotificationType.PaymentsRecurringPaymentsScheduledOtherParty
  | typeof NotificationType.AuthenticationResetPassword
  | typeof NotificationType.AuthenticationVerifyEmail
  | typeof NotificationType.CaseComment
  | typeof NotificationType.ParticipantCaseCommentAssignee
  | typeof NotificationType.ParticipantCaseCommentSupport
  | typeof NotificationType.WelcomeMessage
  | typeof NotificationType.UnsubscribeChannelConfirmation
  | typeof NotificationType.SendSMSHelp
  | typeof NotificationType.NewApplicationsReport;

export interface Notification {
  id: string;
  externalId: string;
  commentId: string;
  comment: Comment;
  createdAt: Date;
  updatedAt?: Date;
}

export interface ChannelConfiguration {
  channel: NotificationChannel;
  template: NotificationTemplate;
  enabled: boolean;
}

export interface NotificationConfig {
  type: PlatformNotificationType;
  name?: string;
  description?: string;
  channelConfigurations: ChannelConfiguration[];
}

export const NotificationTypeFeature: Partial<Record<FeatureName, NotificationType[]>> = {
  [FeatureName.PaymentsClaimFunds]: [
    NotificationType.PaymentsClaimFunds,
    NotificationType.PaymentsFundsClaimed,
    NotificationType.PaymentsApplicantPaymentReset,
  ],
  [FeatureName.PaymentsPartnerIssued]: [
    NotificationType.PaymentsFundsIssued,
    NotificationType.PaymentsAdvocatePaymentReset,
    NotificationType.PaymentSentToOtherParty,
  ],
  [FeatureName.PaymentsRecurring]: [
    NotificationType.PaymentsRecurringPaymentInitiated,
    NotificationType.PaymentsRecurringPaymentsScheduled,
    NotificationType.PaymentsRecurringPaymentsScheduledOtherParty,
  ],
  [FeatureName.PaymentsExternalTracking]: [NotificationType.ApplicationApproved],
  [FeatureName.ProgramsReferral]: [NotificationType.ProgramReferral],
  [FeatureName.ApplicantComments]: [
    NotificationType.ParticipantCaseCommentAssignee,
    NotificationType.ParticipantCaseCommentSupport,
  ],
};

export const DefaultNotificationType = [
  NotificationType.ApplicationDenied,
  NotificationType.ApplicationIncomplete,
  NotificationType.ApplicationSubmitted,
  NotificationType.ApplicationWithdrawn,
  NotificationType.CaseComment,
  NotificationType.MultipartyInvitation,
];

export const MultiPartyNotificationType = [
  NotificationType.MultipartyInvitation,
  NotificationType.PaymentsRecurringPaymentsScheduledOtherParty,
];

/** Enhancement: Add metadata to db and retrieve from there */
export const NotificationTypesMetadata: Record<
  NotificationType,
  { name: string; description: string }
> = {
  [NotificationType.UnknownType]: {
    name: 'Unknown Notification',
    description: "A fallback for notifications that don't fit other categories.",
  },
  [NotificationType.AuthenticationChangePrimaryEmail]: {
    name: 'Email Change Notification',
    description: 'Sent when a user changes their primary email address.',
  },
  [NotificationType.AuthenticationResetPassword]: {
    name: 'Password Reset Request',
    description: 'Notification sent when a user requests to reset their password.',
  },
  [NotificationType.AuthenticationVerifyEmail]: {
    name: 'Email Verification Request',
    description: "Sent to verify a user's email address.",
  },
  [NotificationType.AuthenticationMagicLink]: {
    name: 'Login Magic Link',
    description: 'Provides a secure one-click login link via email.',
  },
  [NotificationType.AuthenticationIdentityVerifyEmail]: {
    name: 'Identity Email Verification',
    description: "Confirms a user's identity through email verification.",
  },
  [NotificationType.ApplicationDenied]: {
    name: 'Application Rejection Notice',
    description: 'Informs an applicant that their application has been denied.',
  },
  [NotificationType.ApplicationIncomplete]: {
    name: 'Incomplete Application Alert',
    description: 'Notifies users about missing information in their application.',
  },
  [NotificationType.ApplicationSubmitted]: {
    name: 'Application Receipt Confirmation',
    description: 'Acknowledges that an application has been successfully submitted.',
  },
  [NotificationType.ApplicationWithdrawn]: {
    name: 'Application Withdrawal Confirmation',
    description: 'Confirms that an application has been withdrawn by the user.',
  },
  [NotificationType.ApplicationApproved]: {
    name: 'Application Approval Notice',
    description: 'Informs an applicant that their application has been approved.',
  },
  [NotificationType.PaymentsClaimFunds]: {
    name: 'Funds Claim Reminder',
    description: 'Notifies users they have funds available to claim.',
  },
  [NotificationType.PaymentsFundsClaimed]: {
    name: 'Funds Claim Confirmation',
    description: 'Confirms that funds have been successfully claimed.',
  },
  [NotificationType.PaymentsFundsIssued]: {
    name: 'Funds Issuance Notice',
    description: 'Informs users that funds have been issued to them.',
  },
  [NotificationType.PaymentSentToOtherParty]: {
    name: 'Other Party Payment Notification',
    description: 'Alerts other parties that a payment has been sent.',
  },
  [NotificationType.PaymentsApplicantPaymentReset]: {
    name: 'Applicant Payment Reset Notice',
    description: 'Informs an applicant that their payment has been reset.',
  },
  [NotificationType.PaymentsAdvocatePaymentReset]: {
    name: 'Advocate Payment Reset Notice',
    description: 'Informs an advocate that a payment has been reset.',
  },
  [NotificationType.PaymentsRecurringPaymentInitiated]: {
    name: 'Recurring Payment Setup Notice',
    description: 'Confirms that a recurring payment has been initiated.',
  },
  [NotificationType.PaymentsRecurringPaymentsScheduled]: {
    name: 'Recurring Payments Schedule Confirmation',
    description: 'Notifies users about their scheduled recurring payments.',
  },
  [NotificationType.PaymentsRecurringPaymentsScheduledOtherParty]: {
    name: 'Other Party Recurring Payments Notice',
    description: 'Informs other parties about recurring payments that have been scheduled.',
  },
  [NotificationType.MultipartyInvitation]: {
    name: 'Collaboration Invitation',
    description: 'Invites a user to join a multiparty collaboration.',
  },
  [NotificationType.ProgramReferral]: {
    name: 'Program Referral Notice',
    description: "Informs users they've been referred to a program.",
  },
  [NotificationType.CaseComment]: {
    name: 'Case Comment Notification',
    description: 'Alerts users about a new comment on their case.',
  },
  [NotificationType.ParticipantCaseCommentAssignee]: {
    name: 'Case Comment Assignment Notice',
    description: 'Informs case assignee to address a case comment.',
  },
  [NotificationType.ParticipantCaseCommentSupport]: {
    name: 'Support Comment Notification',
    description: 'Alerts support staff about a case comment requiring attention.',
  },
  [NotificationType.WelcomeMessage]: {
    name: 'Welcome Onboarding Message',
    description: 'Greets new users with welcome information.',
  },
  [NotificationType.UnsubscribeChannelConfirmation]: {
    name: 'Unsubscribe Confirmation',
    description: 'Confirms a user has been unsubscribed from a communication channel.',
  },
  [NotificationType.SendSMSHelp]: {
    name: 'SMS Help Guide',
    description: 'Provides guidance on SMS messaging features.',
  },
  [NotificationType.NewApplicationsReport]: {
    name: 'New Applications Report',
    description: 'Notifies case workers about new applications.',
  },
};

export default Notification;
